<template>
  <div class="cpd-provider-detail">
    <v-card flat>
      <v-card-text class="cpd-provider-detail__body">
        <div class="cpd-provider-detail__grid">
          <div class="cpd-provider-detail__item">
            <div class="cpd-provider-detail__label">CPD Provider Name</div>
            <div class="cpd-provider-detail__value">
              {{ values.trainingCenterName ? values.trainingCenterName : "-" }}
            </div>
          </div>
          <div class="cpd-provider-detail__item">
            <div class="cpd-provider-detail__label">CPD Provider Status</div>
            <div class="cpd-provider-detail__value">
              <v-chip :color="getColor(values)" dark small>
                {{ getStatusLabel(values) }}
              </v-chip>
            </div>
          </div>
          <div class="cpd-provider-detail__item">
            <div class="cpd-provider-detail__label">Geolocation (Region)</div>
            <div class="cpd-provider-detail__value">
              {{ values?.region?.name ?? values?.woreda?.zone?.region?.name ?? values?.zone?.region?.name ?? "-" }}
            </div>
          </div>
          <div class="cpd-provider-detail__item">
            <div class="cpd-provider-detail__label">Institution Email Address</div>
            <div class="cpd-provider-detail__value">
              {{ values.institutionEmailAddress ? values.institutionEmailAddress : "-" }}
            </div>
          </div>
          <div class="cpd-provider-detail__item">
            <div class="cpd-provider-detail__label">Institution Phone Number</div>
            <div class="cpd-provider-detail__value">
              {{ values.institutionPhoneNumber ? values.institutionPhoneNumber : "-" }}
            </div>
          </div>
          <div class="cpd-provider-detail__item">
            <div class="cpd-provider-detail__label">P.O.BOX</div>
            <div class="cpd-provider-detail__value">
              {{ values.pobox ? values.pobox : "-" }}
            </div>
          </div>
          <div class="cpd-provider-detail__item">
            <div class="cpd-provider-detail__label">Coordinator Name</div>
            <div class="cpd-provider-detail__value">
              {{ values.coordinatorName ? values.coordinatorName : "-" }}
            </div>
          </div>
          <div class="cpd-provider-detail__item">
            <div class="cpd-provider-detail__label">Coordinator Email Address</div>
            <div class="cpd-provider-detail__value">
              {{ values.coordinatorEmailAddress ? values.coordinatorEmailAddress : "-" }}
            </div>
          </div>
          <div class="cpd-provider-detail__item">
            <div class="cpd-provider-detail__label">Coordinator Phone Number</div>
            <div class="cpd-provider-detail__value">
              {{ values.coordinatorPhoneNumber ? values.coordinatorPhoneNumber : "-" }}
            </div>
          </div>
          <div class="cpd-provider-detail__item">
            <div class="cpd-provider-detail__label">Accredited By</div>
            <div class="cpd-provider-detail__value">
              {{ values.accreditedBy ? values.accreditedBy.accreditorName : "-" }}
            </div>
          </div>
          <div class="cpd-provider-detail__item">
            <div class="cpd-provider-detail__label">Accreditation Date</div>
            <div class="cpd-provider-detail__value">
              {{ values.accreditationDate ? values.accreditationDate : "-" }}
            </div>
          </div>
          <div class="cpd-provider-detail__item">
            <div class="cpd-provider-detail__label">CPD Provider's Ownership</div>
            <div class="cpd-provider-detail__value">
              {{ values.cpdProviderOwnership ? values.cpdProviderOwnership : "-" }}
            </div>
          </div>
        </div>
        <div class="cpd-provider-detail__map">
          <div class="cpd-provider-detail__map-header">
            <label class="cpd-provider-detail__label">Location on Map</label>
            <v-btn
                class="cpd-provider-detail__map-toggle"
                text
                small
                type="button"
                @click="toggleMapSize"
            >
              <v-icon left small>{{ mapExpanded ? "mdi-arrow-collapse" : "mdi-arrow-expand" }}</v-icon>
              {{ mapExpanded ? "Minimize map" : "Expand map" }}
            </v-btn>
          </div>
          <div class="cpd-provider-detail__map-coordinates">
            <div class="cpd-provider-detail__coord">
              <span class="cpd-provider-detail__label">Latitude</span>
              <span class="cpd-provider-detail__value">
                {{ values.latitude ? values.latitude : "-" }}
              </span>
            </div>
            <div class="cpd-provider-detail__coord">
              <span class="cpd-provider-detail__label">Longitude</span>
              <span class="cpd-provider-detail__value">
                {{ values.longtiude ? values.longtiude : "-" }}
              </span>
            </div>
          </div>
          <div class="cpd-provider-detail__map-shell" :class="{ 'is-expanded': mapExpanded }">
            <div ref="trainingCenterDetailMap" class="cpd-provider-detail__map-view"></div>
          </div>
          <div class="cpd-provider-detail__map-hint">
            {{ hasCoordinates ? "Map shows stored latitude and longitude." : "No location stored yet." }}
          </div>
        </div>
        <div class="cpd-provider-detail__content">
          <div class="cpd-provider-detail__section-title">
            Provider Images
          </div>
          <div class="cpd-provider-detail__asset-grid">
            <div
                v-for="asset in providerAssets"
                :key="asset.key"
                class="cpd-provider-detail__asset"
            >
              <div class="cpd-provider-detail__asset-header">
                <span class="cpd-provider-detail__label">{{ asset.label }}</span>
                <v-btn
                    v-if="assetPreviews[asset.key]"
                    class="cpd-provider-detail__content-action"
                    small
                    text
                    @click.prevent="openAssetPreview(asset.key)"
                >
                  <v-icon left small>mdi-eye</v-icon>
                  Preview
                </v-btn>
              </div>
              <v-img
                  v-if="assetPreviews[asset.key]"
                  :src="assetPreviews[asset.key]"
                  class="cpd-provider-detail__asset-image"
                  contain
                  max-height="180"
              ></v-img>
              <div v-else class="cpd-provider-detail__empty">
                No image uploaded.
              </div>
            </div>
          </div>
        </div>
        <div class="cpd-provider-detail__content">
          <div class="cpd-provider-detail__section-title">
            Assessment Checklist
          </div>
          <div v-if="checklistPreviewUrl" class="cpd-provider-detail__content-preview">
            <div class="cpd-provider-detail__content-actions">
              <v-btn
                  class="cpd-provider-detail__content-action"
                  small
                  text
                  @click.prevent="openChecklistPreview"
              >
                <v-icon left small>mdi-eye</v-icon>
                Preview
              </v-btn>
            </div>
            <iframe
                class="cpd-provider-detail__content-frame"
                :src="checklistPreviewUrl"
                title="Assessment checklist preview"
            ></iframe>
          </div>
          <div v-else class="cpd-provider-detail__empty">
            No checklist uploaded.
          </div>
        </div>
        <div class="cpd-provider-detail__content">
          <div class="cpd-provider-detail__section-title">
            Renewal History
          </div>
          <v-data-table
              :headers="renewalHeaders"
              :items="renewals"
              :loading="historyLoading"
              class="cpd-provider-detail__history-table elevation-1"
              dense
              hide-default-footer
              no-data-text="No renewal history found."
          >
            <template v-slot:[`item.accreditedDate`]="{ item }">
              {{ formatDate(item.accreditedDate) }}
            </template>
          </v-data-table>
        </div>
        <div class="cpd-provider-detail__content">
          <div class="cpd-provider-detail__section-title">
            Transfer History
          </div>
          <v-data-table
              :headers="transferHeaders"
              :items="transfers"
              :loading="historyLoading"
              class="cpd-provider-detail__history-table elevation-1"
              dense
              hide-default-footer
              no-data-text="No transfer history found."
          >
            <template v-slot:[`item.transferredDate`]="{ item }">
              {{ formatDate(item.transferredDate) }}
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import "../../plugins/leaflet/leaflet.css";
import * as L from "leaflet";
import {buildPreviewUrl, openPresignedUrl} from "@/utils/file";
import {TrainerService} from "../Trainers/trainers.service";
import {TrainingCenterService} from "./training_center.service";

const ETHIOPIA_GEOJSON = {
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      id: "ETH",
      properties: {
        name: "Ethiopia",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [37.90607, 14.95943],
            [38.51295, 14.50547],
            [39.0994, 14.74064],
            [39.34061, 14.53155],
            [40.02625, 14.51959],
            [40.8966, 14.11864],
            [41.1552, 13.77333],
            [41.59856, 13.45209],
            [42.00975, 12.86582],
            [42.35156, 12.54223],
            [42, 12.1],
            [41.66176, 11.6312],
            [41.73959, 11.35511],
            [41.75557, 11.05091],
            [42.31414, 11.0342],
            [42.55493, 11.10511],
            [42.776852, 10.926879],
            [42.55876, 10.57258],
            [42.92812, 10.02194],
            [43.29699, 9.54048],
            [43.67875, 9.18358],
            [46.94834, 7.99688],
            [47.78942, 8.003],
            [44.9636, 5.00162],
            [43.66087, 4.95755],
            [42.76967, 4.25259],
            [42.12861, 4.23413],
            [41.855083, 3.918912],
            [41.1718, 3.91909],
            [40.76848, 4.25702],
            [39.85494, 3.83879],
            [39.559384, 3.42206],
            [38.89251, 3.50074],
            [38.67114, 3.61607],
            [38.43697, 3.58851],
            [38.120915, 3.598605],
            [36.855093, 4.447864],
            [36.159079, 4.447864],
            [35.817448, 4.776966],
            [35.817448, 5.338232],
            [35.298007, 5.506],
            [34.70702, 6.59422],
            [34.25032, 6.82607],
            [34.0751, 7.22595],
            [33.56829, 7.71334],
            [32.95418, 7.78497],
            [33.2948, 8.35458],
            [33.8255, 8.37916],
            [33.97498, 8.68456],
            [33.96162, 9.58358],
            [34.25745, 10.63009],
            [34.73115, 10.91017],
            [34.83163, 11.31896],
            [35.26049, 12.08286],
            [35.86363, 12.57828],
            [36.27022, 13.56333],
            [36.42951, 14.42211],
            [37.59377, 14.2131],
            [37.90607, 14.95943],
          ],
        ],
      },
    },
  ],
};

export default {
  props: {
    trainingCenter: Object,
  },
  data() {
    return {
      values: {},
      map: null,
      mapMarker: null,
      mapExpanded: false,
      ethiopiaBounds: null,
      checklistPreviewUrl: null,
      assetPreviews: {
        logo: null,
        stamp: null,
        signature1: null,
        signature2: null,
      },
      providerAssets: [
        {key: "logo", label: "CPD Provider's Logo", pathKey: "logo_url"},
        {key: "stamp", label: "CPD Provider's Stamp", pathKey: "stamp_url"},
        {key: "signature1", label: "CPD Provider's Signature 1", pathKey: "signature1_url"},
        {key: "signature2", label: "CPD Provider's Signature 2", pathKey: "signature2_url"},
      ],
      historyLoading: false,
      renewals: [],
      transfers: [],
      renewalHeaders: [
        {text: "Previous Accredited Date", value: "accreditedDate"},
        {text: "Accredited By", value: "user.firstName"},
      ],
      transferHeaders: [
        {text: "Transferred From", value: "accreditorFrom.accreditorName"},
        {text: "Transferred To", value: "accreditorTo.accreditorName"},
        {text: "Transferred Date", value: "transferredDate"},
        {text: "Transferred By", value: "user.firstName"},
      ],
    };
  },
  computed: {
    hasCoordinates() {
      const lat = Number(this.values?.latitude);
      const lng = Number(this.values?.longtiude);
      return Number.isFinite(lat) && Number.isFinite(lng);
    },
  },
  watch: {
    trainingCenter: {
      immediate: true,
      handler(value) {
        this.values = value || {};
        if (this.map) {
          this.setMapMarkerFromValues();
        }
        this.prefetchChecklistPreview();
        this.prefetchAssetPreviews();
        this.loadHistory();
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initMap();
    });
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    getStatusLabel(item) {
      if (item?.statusLabel) {
        return item.statusLabel;
      }
      return item?.isActive === false ? "Not Active" : "Active";
    },
    getColor(item) {
      const status = this.getStatusLabel(item);
      if (status === "Expired") return "orange darken-2";
      if (status === "Not Active") return "red";
      return "green";
    },
    extractHistoryRows(response) {
      const payload = response?.data?.data ?? response?.data ?? [];
      return Array.isArray(payload) ? payload : [];
    },
    formatDate(value) {
      if (!value) return "-";
      return value.toString().split("T")[0];
    },
    async loadHistory() {
      const providerId = this.values?.id;
      if (!providerId) {
        this.renewals = [];
        this.transfers = [];
        return;
      }

      this.historyLoading = true;
      try {
        const [renewalResponse, transferResponse] = await Promise.all([
          TrainingCenterService.getRenewals(providerId),
          TrainingCenterService.getTransfers(providerId),
        ]);
        this.renewals = this.extractHistoryRows(renewalResponse);
        this.transfers = this.extractHistoryRows(transferResponse);
      } catch (error) {
        this.renewals = [];
        this.transfers = [];
      } finally {
        this.historyLoading = false;
      }
    },
    getChecklistPath() {
      return (
          this.values?.checklist ||
          this.values?.checklist_url ||
          null
      );
    },
    async prefetchChecklistPreview() {
      const path = this.getChecklistPath();
      if (!path) {
        this.checklistPreviewUrl = null;
        return;
      }
      const url = await buildPreviewUrl(path, {
        preferHttps: false,
        allowInsecure: true,
      });
      this.checklistPreviewUrl = url;
    },
    getAssetPath(key) {
      const asset = this.providerAssets.find((item) => item.key === key);
      return asset ? this.values?.[asset.pathKey] : null;
    },
    async buildAssetPreview(path) {
      return buildPreviewUrl(path, {
        preferHttps: false,
        allowInsecure: true,
        fetchImage: (url) => TrainerService.getImage({url}),
      });
    },
    async prefetchAssetPreviews() {
      await Promise.all(
          this.providerAssets.map(async (asset) => {
            const path = this.values?.[asset.pathKey];
            if (!path) {
              this.$set(this.assetPreviews, asset.key, null);
              return;
            }
            const url = await this.buildAssetPreview(path);
            this.$set(this.assetPreviews, asset.key, url || null);
          })
      );
    },
    async openAssetPreview(key) {
      const path = this.getAssetPath(key);
      if (path) {
        await openPresignedUrl(path, {preferHttps: false, allowInsecure: true});
      }
    },
    async openChecklistPreview() {
      const path = this.getChecklistPath();
      if (path) {
        await openPresignedUrl(path, {preferHttps: false, allowInsecure: true});
      }
    },
    initMap() {
      if (!this.$refs.trainingCenterDetailMap) return;
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
      const ethiopiaLayer = L.geoJSON(ETHIOPIA_GEOJSON, {interactive: false});
      const ethiopiaBounds = ethiopiaLayer.getBounds();
      this.ethiopiaBounds = ethiopiaBounds;
      const map = L.map(this.$refs.trainingCenterDetailMap, {
        attributionControl: false,
        maxBounds: ethiopiaBounds,
        maxBoundsViscosity: 1.0,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {}).addTo(map);
      ethiopiaLayer.addTo(map);

      const minZoom = map.getBoundsZoom(ethiopiaBounds);
      map.setMinZoom(minZoom);
      map.fitBounds(ethiopiaBounds, {padding: [12, 12]});
      this.map = map;

      this.setMapMarkerFromValues();
    },
    toggleMapSize() {
      this.mapExpanded = !this.mapExpanded;
      this.$nextTick(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      });
    },
    setMapMarkerFromValues() {
      if (!this.map) return;
      const lat = Number(this.values?.latitude);
      const lng = Number(this.values?.longtiude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        if (this.mapMarker) {
          this.map.removeLayer(this.mapMarker);
          this.mapMarker = null;
        }
        return;
      }
      this.setMapMarker(lat, lng);
    },
    setMapMarker(lat, lng) {
      if (!this.map) return;
      const latLng = L.latLng(lat, lng);
      if (!this.mapMarker) {
        this.mapMarker = L.circleMarker(latLng, {
          radius: 8,
          color: "#1976d2",
          fillColor: "#1976d2",
          fillOpacity: 0.75,
        }).addTo(this.map);
      } else {
        this.mapMarker.setLatLng(latLng);
      }
      this.map.panTo(latLng);
    },
  },
};
</script>

<style scoped>
.cpd-provider-detail__body {
  padding: 16px 20px 24px;
}

.cpd-provider-detail__grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 12px;
}

@media (min-width: 768px) {
  .cpd-provider-detail__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.cpd-provider-detail__item {
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  min-height: 64px;
}

.cpd-provider-detail__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 6px;
  display: block;
}

.cpd-provider-detail__value {
  font-size: 13px;
  color: #0f172a;
}

.cpd-provider-detail__map {
  margin-top: 16px;
}

.cpd-provider-detail__map-shell {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #f8fafc;
}

.cpd-provider-detail__map-coordinates {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.cpd-provider-detail__coord {
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
}

.cpd-provider-detail__map-view {
  height: 260px;
  width: 100%;
}

.cpd-provider-detail__map-shell.is-expanded .cpd-provider-detail__map-view {
  height: 70vh;
}

.cpd-provider-detail__map-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.cpd-provider-detail__map-toggle {
  color: #1976d2 !important;
  text-transform: none;
  font-weight: 600;
}

.cpd-provider-detail__map-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #475569;
}

.cpd-provider-detail__content {
  margin-top: 16px;
}

.cpd-provider-detail__section-title {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.cpd-provider-detail__content-preview {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px;
  background: #f8fafc;
}

.cpd-provider-detail__asset-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 12px;
}

@media (min-width: 768px) {
  .cpd-provider-detail__asset-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.cpd-provider-detail__asset {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 10px;
}

.cpd-provider-detail__asset-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 30px;
}

.cpd-provider-detail__asset-image {
  margin-top: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #ffffff;
}

.cpd-provider-detail__content-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 6px;
}

.cpd-provider-detail__content-action {
  color: #1976d2 !important;
  font-weight: 600;
  text-transform: none;
}

.cpd-provider-detail__content-frame {
  width: 100%;
  min-height: 320px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
}

.cpd-provider-detail__history-table {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.cpd-provider-detail__empty {
  font-size: 13px;
  color: #64748b;
}

@media (max-width: 700px) {
  .cpd-provider-detail__map-coordinates {
    grid-template-columns: 1fr;
  }
}
</style>
